import React, { useMemo } from "react";
import { paletteFor, VideoIcon, VideoPalette } from "../../components/VideoArt";

// Re-uses the inline SVG generator from the existing VideoArt RN component,
// but renders into a plain <img> so the web shell stays React-only.

function buildSvg(icon: VideoIcon, palette: VideoPalette): string {
  const [bg, , light] = palette;
  const W = 400;
  const H = 250;
  const cx = W / 2;
  const cy = H / 2;
  const s = 2;

  const gradientId = `vg-${icon}-${bg.replace("#", "")}`;
  const defs =
    `<defs>` +
    `<linearGradient id="${gradientId}" x1="0" y1="0" x2="0" y2="1">` +
    `<stop offset="0" stop-color="${light}"/>` +
    `<stop offset="1" stop-color="${bg}"/>` +
    `</linearGradient>` +
    `</defs>`;

  const rect = `<rect width="${W}" height="${H}" fill="url(#${gradientId})"/>`;
  const decor =
    `<circle cx="${W * 0.12}" cy="${H * 0.18}" r="${H * 0.08}" fill="rgba(255,255,255,0.35)"/>` +
    `<circle cx="${W * 0.88}" cy="${H * 0.78}" r="${H * 0.10}" fill="rgba(255,255,255,0.25)"/>`;

  let shape = "";
  if (icon === "cookies") {
    const cookie = (x: number, y: number, r: number) =>
      `<g transform="translate(${x},${y})">` +
      `<circle r="${r}" fill="#C58850"/>` +
      `<circle r="${r - 4}" fill="#E1A668"/>` +
      `<circle cx="${(Math.cos(0) * r) / 2}" cy="${(Math.sin(0) * r) / 2}" r="${r * 0.18}" fill="#5b3a1a"/>` +
      `<circle cx="${(Math.cos(2.1) * r) / 2}" cy="${(Math.sin(2.1) * r) / 2}" r="${r * 0.18}" fill="#5b3a1a"/>` +
      `<circle cx="${(Math.cos(4.2) * r) / 2}" cy="${(Math.sin(4.2) * r) / 2}" r="${r * 0.18}" fill="#5b3a1a"/>` +
      `</g>`;
    shape =
      cookie(cx - 36 * s, cy + 6 * s, 16 * s) +
      cookie(cx + 6 * s, cy - 8 * s, 18 * s) +
      cookie(cx + 32 * s, cy + 14 * s, 14 * s);
  } else if (icon === "letterB") {
    shape = `<text x="${cx}" y="${cy + 24 * s}" font-family="Nunito, sans-serif" font-weight="900" font-size="${84 * s}" text-anchor="middle" fill="#fff">B</text>`;
  } else if (icon === "rainbow") {
    const arc = (r: number, c: string) =>
      `<path d="M ${cx - r} ${cy + 14 * s} A ${r} ${r} 0 0 1 ${cx + r} ${cy + 14 * s}" stroke="${c}" stroke-width="${8 * s}" fill="none" stroke-linecap="round"/>`;
    shape = arc(40 * s, "#FF6B6B") + arc(30 * s, "#FFD86B") + arc(20 * s, "#7AC74F");
  } else if (icon === "shapes") {
    shape =
      `<circle cx="${cx - 30 * s}" cy="${cy}" r="${16 * s}" fill="#FF8A4C"/>` +
      `<rect x="${cx - 8 * s}" y="${cy - 16 * s}" width="${32 * s}" height="${32 * s}" rx="4" fill="#5B8DEF"/>` +
      `<polygon points="${cx + 44 * s},${cy - 16 * s} ${cx + 60 * s},${cy + 16 * s} ${cx + 28 * s},${cy + 16 * s}" fill="#F18EB6"/>`;
  } else if (icon === "moon") {
    shape =
      `<circle cx="${cx + 8 * s}" cy="${cy - 4 * s}" r="${26 * s}" fill="#FFE9B0"/>` +
      `<circle cx="${cx + 18 * s}" cy="${cy - 10 * s}" r="${26 * s}" fill="${bg}"/>`;
  } else if (icon === "hands") {
    shape = `<circle cx="${cx}" cy="${cy + 10 * s}" r="${10 * s}" fill="#FFD86B"/>`;
  } else if (icon === "mountain") {
    shape =
      `<polygon points="${cx - 50 * s},${cy + 22 * s} ${cx - 14 * s},${cy - 26 * s} ${cx + 12 * s},${cy + 22 * s}" fill="#3D6E70"/>` +
      `<polygon points="${cx - 6 * s},${cy + 22 * s} ${cx + 26 * s},${cy - 16 * s} ${cx + 54 * s},${cy + 22 * s}" fill="#5B8B89"/>`;
  } else if (icon === "pencil") {
    shape =
      `<g transform="rotate(-30 ${cx} ${cy})">` +
      `<rect x="${cx - 50 * s}" y="${cy - 8 * s}" width="${10 * s}" height="${16 * s}" fill="#FF6B6B"/>` +
      `<rect x="${cx - 40 * s}" y="${cy - 8 * s}" width="${68 * s}" height="${16 * s}" fill="#FFD86B"/>` +
      `<polygon points="${cx + 28 * s},${cy - 8 * s} ${cx + 44 * s},${cy} ${cx + 28 * s},${cy + 8 * s}" fill="#2E2316"/>` +
      `</g>`;
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" style="display:block">` +
    defs +
    rect +
    decor +
    shape +
    `</svg>`
  );
}

export function WebVideoArt({ category }: { category: string }) {
  const { palette, icon } = useMemo(() => paletteFor(category), [category]);
  // Render the SVG INLINE (not via an <img src="data:…">). A data-URI image
  // can fail to load behind Cloudflare / a strict CSP and leave a broken-image
  // icon — an inline <svg> is real DOM and can't fail. The svg itself is sized
  // 100% (see buildSvg) so it fills the container (card, spotlight, or the
  // full player stage when a teaching video has no clip).
  const svg = useMemo(() => buildSvg(icon, palette), [icon, palette]);
  return (
    <div
      style={{ width: "100%", height: "100%" }}
      // SVG is built from a fixed internal palette/icon — no user input.
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

export default WebVideoArt;
